import { Component, Input, Output, EventEmitter, SimpleChanges, ChangeDetectionStrategy, SimpleChange, ViewChild, ElementRef, OnChanges, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ClipboardService } from 'ngx-clipboard';
import { ChartConfigBase } from '../../models/chart-config-base';
import { XmlReportDataService } from '../../services/xml-report-data.service';
import { HighChartDataBase } from '../../models/high-chart-data-base';
import { ClickDataPoint, ClickNotifier } from '../../models/click-notifier';
import { DrillDownInfo } from '../../models/drilldown-info';
import { ElearningInfo } from '../../models/elearning-info';

import * as Highcharts from 'highcharts'; // Obs! Används i template
import { CommonModule, AsyncPipe } from '@angular/common';
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap/nav';
import { SpinnerComponent } from '../spinner/spinner.component';
import { HighchartsChartComponent } from 'highcharts-angular';
import { ChartTotalSumComponent } from '../chart-total-sum/chart-total-sum.component';
import { ChartInfoComponent } from '../chart-info/chart-info.component';
import { ChartHeightComponent } from '../chart-height/chart-height.component';
import { ChartShowTableComponent } from '../chart-show-table/chart-show-table.component';
import { ChartShowDetailedTableComponent } from '../chart-show-detailed-table/chart-show-detailed-table.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'xml-report',
    styleUrls: ['./xml-report.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './xml-report.html',
    imports: [NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, SpinnerComponent, HighchartsChartComponent, ChartTotalSumComponent, ChartInfoComponent, ChartHeightComponent, ChartShowTableComponent, ChartShowDetailedTableComponent, NgbNavOutlet, AsyncPipe, TranslatePipe]
})
export class XmlReportComponent implements OnChanges, OnDestroy {

    @Input() config: ChartConfigBase;
    @Input() productionMode: boolean = false;
    @Input() chartOnly: boolean = false;
    @Input() currentChartHeight: string;
    @Input() drillDownInfo: DrillDownInfo;
    @Input() elearningInfo: ElearningInfo;
    @Input() language: string;
    @Output() exportChartData = new EventEmitter();
    @Output() clickedCategory = new EventEmitter();
    @Output() clickedSerie = new EventEmitter();
    @Output() loadReportError = new EventEmitter();
    @Output() drillUp = new EventEmitter();
    @Output() chartHeightChange = new EventEmitter();

    @ViewChild('code', {static: false}) code: ElementRef;

    public activeTab = 1;
    public Highcharts = Highcharts;
    public chartOptions = new BehaviorSubject<Object>(null);
    public chartOptionsIsUpdated: boolean = false;
    public chartData = new BehaviorSubject<HighChartDataBase>(null);
    public descriptionHtml = new BehaviorSubject<string>(null);
    public isLoading = new BehaviorSubject<boolean>(null);
    public isShowingSql: boolean = false;
    public isShowingTable = false;
    public showInfo: boolean = false;

    public clickNotifier = new ClickNotifier((clickedPoint: ClickDataPoint) => {
        this.clickedCategory.emit(clickedPoint.category);
        if (clickedPoint.serie) this.clickedSerie.emit(clickedPoint);
    });

    public sqlIsCopied: boolean = false;

    constructor(
        private xmlService: XmlReportDataService,
        private clipboardService: ClipboardService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        let config: SimpleChange = changes['config'];
        if (config) {
            if (config.previousValue &&
                (config.currentValue['reportName'] !== config.previousValue['reportName'] ||
                config.currentValue['language'] !== config.previousValue['language'])) {
                this.xmlService.currentGroupings.next(null); // Ny rapport eller nytt språk, rensa groupings innan uppdatering
            }
            this.updateChart();
        }
    }

    ngAfterViewChecked(): void {
        if (this.drillDownInfo.canDrillDown)
            this.setCursorOnChartPoints();
    }

    ngOnDestroy() {
        this.getChartDataSubscription?.unsubscribe();
    }

    public reportHasErrors = new BehaviorSubject<boolean>(false);
    public reportHasBelowMinDataAmount = new BehaviorSubject<boolean>(false);
    public reportError: string;
    private getChartDataSubscription: Subscription;
    public updateChart() {
        if (!this.config || !this.config.reportName) {
            return;
        }
        this.reportHasErrors.next(false);
        this.reportHasBelowMinDataAmount.next(false);

        this.isLoading.next(true);

        if (this.getChartDataSubscription) {
            //Om ett anrop redan är ivägskickat, avbryt det anropet innan ett nytt skickas iväg
            this.getChartDataSubscription.unsubscribe();
        }

        this.getChartDataSubscription = this.xmlService.getHighChartData(this.config, this.clickNotifier).subscribe((data: HighChartDataBase) => {
            this.chartData.next(data); // Spara ner för ev framtida bruk
            this.exportChartData.emit(data); // Skicka chartdata till utanför komponenten
            this.chartOptions.next(data.getHighChartOptions()); // Hämta ut graf-data + options och skicka till highcharts
            this.chartOptionsIsUpdated = true;
            if (this.chartOnly == false) {
                this.descriptionHtml.next(data.descriptionHtml);
                this.sqlIsCopied = false;
                this.activeTab = 1;
            }
            this.isLoading.next(false);
        }, (error) => {
            this.reportHasErrors.next(true);
            this.reportError = error;
            if (error && (typeof(error) === 'string') && (error.indexOf('datamängd') > -1 || error.indexOf('data amount') > -1)) {
                this.reportHasBelowMinDataAmount.next(true);
            }
            this.isLoading.next(false);
        });
    }

    public updateIsLoading(isLoading: boolean) {
        this.isLoading.next(isLoading);
    }

    public infoZoomScrollIsRelevant(): boolean {
        let isRelevant = true;
        let chartData = this.chartData.getValue();
        if (!chartData) return false;
        if (chartData.chartType == "radar") isRelevant = false;
        return isRelevant;
    }

    public copySql(): void {
        let codeText = (this.code != undefined) ? this.code.nativeElement.innerText : "";
        this.clipboardService.copy(codeText);
        this.sqlIsCopied = true;
    }

    public getExcel(): void {
        let data = this.chartData.getValue();
        this.xmlService.getExcelTable(this.config).subscribe((blob: Blob) => {
            const fileName = `${data.chartTitle}-${data.chartSubTitle}.xlsx`;
            const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
            let url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        });
    }

    private setCursorOnChartPoints() {
        let chartPoints = document.getElementsByClassName("highcharts-point");
        for (var i = 0; i < chartPoints.length; i++) {
            chartPoints[i].setAttribute("style", "cursor: pointer");
        }
    }

}
